import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { cn } from '@/shared/utils/cn';

export type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: ReactNode;
  emptyAction?: ReactNode;
  onRowClick?: (row: T) => void;
  className?: string;
  getRowId?: (row: T, index: number) => string;
  numbered?: boolean;
  pageIndex?: number;
  pageSize?: number;
};

export const DataTable = <T,>({
  data,
  columns,
  isLoading = false,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  emptyAction,
  onRowClick,
  className,
  getRowId,
  numbered = false,
  pageIndex,
  pageSize,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);

  const tableColumns = useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!numbered) return columns;
    const indexCol: ColumnDef<T, unknown> = {
      id: '__index',
      header: '#',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-ink-faint">
          {(pageIndex ?? 0) * (pageSize ?? Math.max(data.length, 1)) + row.index + 1}
        </span>
      ),
    };
    return [indexCol, ...columns];
  }, [columns, numbered, pageIndex, pageSize, data.length]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: getRowId ? (row, index) => getRowId(row, index) : undefined,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-2xl border border-slate-200 bg-white">
        <Spinner size="lg" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle ?? t('ui.nothingFound')}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return (
    <div className={cn('overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card', className)}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortDir = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={cn(
                        'border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted',
                        canSort && 'cursor-pointer select-none',
                      )}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="text-ink-faint">
                            {sortDir === 'asc' ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : sortDir === 'desc' ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronsUpDown className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn(
                  'border-b border-slate-100 transition-colors last:border-0',
                  onRowClick && 'cursor-pointer hover:bg-brand-50/40',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 text-ink-soft">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
